import {
  INTERNAL_PREST0N_INSTANCE_IDS,
  INTERNAL_PREST0N_SOURCES,
} from '../components/GlobalChatSystem/types';

export interface ThreadSwitcherCapabilityInput {
  pathname: string;
  source?: string;
  instanceId?: string;
  /**
   * No longer consulted (2026-07-13, chat-surface uniformity): anonymous
   * visitors get the same thread experience as signed-in users — threads
   * live in unscoped localStorage until sign-in migrates them to the
   * uid-scoped keys. Kept in the input shape so callers don't churn.
   */
  isAuthenticated?: boolean;
}

/**
 * Shared internal-Prest0n guard for thread-switcher eligibility.
 *
 * NOTE: This must stay in lockstep with the existing force-off behavior:
 * `INTERNAL_PREST0N_SOURCES` / `INTERNAL_PREST0N_INSTANCE_IDS` plus the
 * defensive vm/citadel pattern checks.
 */
export function isInternalPrest0nSurface(source?: string, instanceId?: string): boolean {
  const normalizedSource = (source || '').toLowerCase();
  const normalizedInstanceId = (instanceId || '').toLowerCase();

  if (INTERNAL_PREST0N_SOURCES.has(normalizedSource)) return true;
  if (INTERNAL_PREST0N_INSTANCE_IDS.has(normalizedInstanceId)) return true;
  if (/(^|[_-])vm([_-]|$)/.test(normalizedInstanceId) || /(^|[_-])vm([_-]|$)/.test(normalizedSource)) {
    return true;
  }
  if (normalizedInstanceId.startsWith('citadel') || normalizedSource.startsWith('citadel')) return true;
  return false;
}

/**
 * Thread-switcher capability — UNIFORM across the web app (owner directive,
 * Miles 2026-07-13): "all chat functionality needs to be available
 * consistently on all pages … including literally every public page."
 *
 * History of why this matters: the previous per-route matrix (FORCE_OFF on
 * `/`,`/pricing`,`/team`,`/infrastructure`; all NON_PRODUCT sources; all
 * unauthenticated visitors) flipped the ONE persistent chat mount between
 * two different history systems as the user navigated: threads OFF routes
 * ran the legacy single-bucket ConversationHistoryContext sync, threads ON
 * routes ran useChatThreads. Crossing home → /desktop mid-session poured
 * the home-page conversation into whichever thread /desktop restored —
 * duplicate "hello" threads, an old thread hijacking fresh content, and
 * chips appearing/disappearing per page. One surface, one system: threads
 * are ON everywhere except internal Prest0n surfaces and the auth flow.
 */
export function resolveThreadSwitcherCapability({
  pathname,
  source,
  instanceId,
}: ThreadSwitcherCapabilityInput): boolean {
  const normalizedPathname = pathname || '/';

  if (isInternalPrest0nSurface(source, instanceId)) return false;

  // The auth flow renders the widget hidden (CHAT_EXCLUDED_ROUTES) — keep
  // the thread store from booting into a half-visible surface there.
  if (normalizedPathname === '/auth' || normalizedPathname.startsWith('/auth/')) return false;

  return true;
}
